// #region: Class definition *********************************************************************/
/**
 * @class DropDown
 * @description Custom web component that provides a 'drop down' menu.
 * 
 * @extends WebComponent
 * Extends the WebComponent base class.
 *
 * @example
 * <drop-down icon="some-icon" label="some-label">
 *     <div class="drop-down-content">
 *         ...
 *     </div>
 * </drop-down>
 *
 * @property {string} icon - An icon to show on the button.
 * @property {string} label - The text to show on the button.
 *
 * @remarks
 *     The attributes are optional.
*/
class DropDown extends WebComponent {

    // #region: Private Fields *******************************************************************/
    /**
     * When dropdown content fits, what alignment to use.
     * @type {boolean}
     * 
     * @private
     */
    #alignRight;

    /**
     * Bound reference to the button key down handler.
     * @type {Function}
     * 
     * @private
     */
    #boundHandleButtonKeyDown;

    /**
     * Bound reference to the menu key down handler.
     * @type {Function}
     * 
     * @private
     */
    #boundHandleMenuKeyDown;

    /**
     * Bound reference to the outside click handler.
     * @type {Function}
     * 
     * @private
     */
    #boundOutsideClickHandler;

    /**
     * The dropdown button element.
     * @type {HTMLButtonElement|null}
     * 
     * @private
     */
    #button;

    /**
     * The dropdown button reference element (existing in the document).
     * @type {HTMLButtonElement|null}
     * 
     * @private
     */
    #buttonReference;

    /**
     * Unique identifier for the menu.
     * @type {string}
     * 
     * @private
     */
    #menuId;

    /**
     * Array of enabled and visible menu items.
     * @type {HTMLElement[]}
     * 
     * @private
     */
    #menuItems;

    /**
     * Overlay element for iOS touch handling.
     * @type {HTMLDivElement|null}
     * 
     * @private
     */
    #overlay;

    /**
     * Cached locale resources for this component.
     * @type {Object}
     *
     * @private
     */
    #locales;
    // #endregion ********************************************************************************/
    
    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of DropDown.
     * @param {string} [buttonReference] Optional selector for existing button element.
     */
    constructor(buttonReference) {
        super();

        // Register locale namespaces used by this component and cache resources.
        this.#locales = this.registerLocaleNamespace();

        // When dropdown content fits, what alignment to use.
        this.#alignRight = false;

        // Unique identifier.
        this.#menuId = `menu-${crypto.randomUUID()}`;

        // Store buttonReference if given;
        this.#buttonReference = buttonReference;

        // Create drop down button or use existing?
        if (this.#buttonReference) {
            this.#button = this.querySelector(`${this.#buttonReference}`);
        }
        else {
            this.#button = document.createElement('button');
            this.#button.type = 'button';
            this.#button.classList.add('button');

            if (this.icon) {
                this.#button.innerHTML += `<span class="button-icon ${this.icon}"></span>`;
            }

            if (this.label) {
                this.#button.innerHTML += `<span class="button-label">${this.label}</span>`;
                this.#button.innerHTML += '<span class="more-icon lf-icon-arrow-drop-down"></span>';
            }
            else {
                this.#button.classList.add('button-icon-only');
            }

            this.insertBefore(this.#button, this.firstChild);
        }
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     * @memberof DropDown
     */
    static tagName = 'drop-down';

    /**
     * Identifies class instance.
     * @returns {string} The string representation of this class instance.
     * 
     * @memberof DropDown
     */
    toString() {
        return '[DropDown]';
    }

    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     * @memberof DropDown
     */
    static toString() {
        return '[DropDown]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class.
     * 
     * @static
     * @memberof DropDown
     */
    static version() {
        return '1.1.0';
    }
    // #endregion ********************************************************************************/

    // #region: Properties ***********************************************************************/
    /**
     * Gets the dropdown default alignment.
     * @returns {boolean} The alignment setting.
     * 
     * @memberof DropDown
     */
    get alignRight() {
        return this.#alignRight;
    }

    /**
     * Sets the dropdown default alignment.
     * @param {boolean} value The alignment to set.
     * 
     * @memberof DropDown
     */
    set alignRight(value) {
        this.#alignRight = value;
    }

    /**
     * Gets the internal active state.
     * @returns {boolean} Returns true if the menu is active, false otherwise.
     * 
     * @memberof DropDown
     */
    get isActive() {
        return this.classList.contains('active');
    }

    /**
     * Sets the active state of the menu.
     * @param {boolean} value True to activate, false to deactivate.
     * 
     * @memberof DropDown
     */
    set isActive(value) {
        if (value) {
            this.classList.add('active');
            this.#button.setAttribute('aria-expanded', 'true');

            // Create a plane to catch all 'outside' menu events (for iOS only).
            if ('ontouchstart' in document.documentElement) {
                this.#overlay = document.createElement('div');
                this.#overlay.style.cssText = `
                    display: block;
                    inset-block-end: 0;
                    inset-block-start: 0;
                    inset-inline-end: 0;
                    inset-inline-start: 0;
                    position: fixed;
                    z-index: 2;
                `;

                this.parentNode.appendChild(this.#overlay);
                this.#overlay.addEventListener('click', this.#boundOutsideClickHandler);
            }
            else {
                // Hide menu when clicked outside.
                document.addEventListener('click', this.#boundOutsideClickHandler);
            }

            this.#adjustMenuPosition();
        }
        else {
            this.classList.remove('active');
            this.#button.setAttribute('aria-expanded', 'false');

            if ('ontouchstart' in document.documentElement) {
                this.#overlay.removeEventListener('click', this.#boundOutsideClickHandler);
                this.parentNode.removeChild(this.#overlay);
            }
            else {
                document.removeEventListener('click', this.#boundOutsideClickHandler);
            }
        }
    }

    /**
     * Gets the icon class name on the button.
     * @returns {string|null} The icon class name or null if not set.
     * 
     * @memberof DropDown
     */
    get icon() {
        return this.getAttribute('icon');
    }

    /**
     * Sets the icon class name on the button.
     * @param {string} value The icon class name to set.
     * 
     * @memberof DropDown
     */
    set icon(value) {
        this.setAttribute('icon', value);
    }

    /**
     * Gets the label.
     * @returns {string|null} The label text or null if not set.
     * 
     * @memberof DropDown
     */
    get label() {
        return this.getAttribute('label');
    }

    /**
     * Sets the label.
     * @param {string} value The label text to set.
     * 
     * @memberof DropDown
     */
    set label(value) {
        this.setAttribute('label', value);
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Called when observed attributes change.
     * @param {string} name The name of the attribute that changed.
     * @param {string|null} oldValue The previous value of the attribute.
     * @param {string|null} newValue The new value of the attribute.
     */
    attributeChangedCallback(name, oldValue, newValue) {
        // Call parent's implementation.
        super.attributeChangedCallback(name, oldValue, newValue);

        if (oldValue === newValue) {
            return;
        }

        switch (name) {

            case 'disabled':
            case 'read-only':
                // Base class handles the attribute, we just apply the state.
                if (this.readOnly || this.disabled) {
                    this.removeEventListener('click', this.toggleMenu);
                    this.removeEventListener('keydown', this.#boundHandleMenuKeyDown);
                    this.#button.removeEventListener('keydown', this.#boundHandleButtonKeyDown);
                }
                else {
                    this.addEventListener('click', this.toggleMenu);
                    this.addEventListener('keydown', this.#boundHandleMenuKeyDown);
                    this.#button.addEventListener('keydown', this.#boundHandleButtonKeyDown);
                }

                break;

            default:
                break;

        }
    }

    /**
     * Called when the custom element is attached to the DOM.
     * 
     * @memberof DropDown
     */
    connectedCallback() {
        super.connectedCallback();

        // Set component role.
        this.setAttribute('role', 'none');
    }

    /**
     * Called when the component is removed from the DOM.
     * 
     * @memberof DropDown
     */
    disconnectedCallback() {
        super.disconnectedCallback();

        // Cleanup event handlers.
        this.removeEventListener('click', this.toggleMenu);
        this.removeEventListener('keydown', this.#boundHandleMenuKeyDown);

        this.#button?.removeEventListener('keydown', this.#boundHandleButtonKeyDown);
        document.removeEventListener('click', this.#boundOutsideClickHandler);

        // Cleanup overlay.
        if (this.#overlay) {
            this.#overlay.removeEventListener('click', this.#boundOutsideClickHandler);

            // Remove overlay.
            if (this.#overlay.parentNode) {
                this.parentNode.removeChild(this.#overlay);
            }
        }
    }

    /**
     * Initialize the UI (on 'DOMContentLoaded').
     * 
     * @memberof DropDown
     */
    initialize() {
        // Set correct context for event handlers and store them so they can be reused and removed.
        this.#boundOutsideClickHandler = this.#outsideClickHandler.bind(this);
        this.#boundHandleButtonKeyDown = this.handleButtonKeyDown.bind(this);
        this.#boundHandleMenuKeyDown = this.handleMenuKeyDown.bind(this);

        // Setup menu content accessibility.
        this.setupAriaAttributes();

        // Add event handlers.
        if (!this.readOnly && !this.disabled) {
            this.addEventListener('click', this.toggleMenu);
            this.addEventListener('keydown', this.#boundHandleMenuKeyDown);
            this.#button.addEventListener('keydown', this.#boundHandleButtonKeyDown);
        }
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Handles key down events on the button.
     * @param {KeyboardEvent} event The keyboard event data.
     * 
     * @memberof DropDown
     */
    handleButtonKeyDown(event) {
        switch (event.key) {

            case ' ':
            case 'ArrowDown':
            case 'Enter':
                {
                    // These keys should all open the menu.
                    event.preventDefault();
                    event.stopPropagation();

                    this.isActive = true;

                    // Focus first menu item when opened with arrow down.
                    const firstMenuItem = this.querySelector('li:not([disabled]):not(.disabled):not([hidden]):not(.hidden)');
                    if (firstMenuItem) {
                        firstMenuItem.focus();
                    }
                }

                break;

            case 'Escape':
                // This one should close the menu.
                event.preventDefault();
                event.stopPropagation();

                this.isActive = false;

                break;

            default:
                break;
        
        }
    }

    /**
     * Handles key down events on the menu/content.
     * @param {KeyboardEvent} event The keyboard event data.
     * 
     * @memberof DropDown
     */
    handleMenuKeyDown(event) {
        if (!this.isActive) {
            return;
        }

        const menuItemsLength = this.#menuItems.length;
        const currentIndex = this.#menuItems.indexOf(document.activeElement);

        switch (event.key) {

            case 'ArrowDown':
                {
                    event.preventDefault();
                    event.stopPropagation();

                    const nextIndex = (currentIndex + 1) % menuItemsLength;

                    this.#menuItems[nextIndex].focus();
                }

                break;

            case 'ArrowUp':
                {
                    event.preventDefault();
                    event.stopPropagation();

                    const prevIndex = (currentIndex - 1 + menuItemsLength) % menuItemsLength;

                    this.#menuItems[prevIndex].focus();
                }

                break;

            case 'Enter':
            case ' ':
                this.#handleEnter(event);

                break;

            case 'Escape':
                event.preventDefault();
                event.stopPropagation();

                this.isActive = false;
                this.#button.focus();

                break;

            case 'Tab':
                event.preventDefault();
                event.stopPropagation();

                this.isActive = false;

                break;

            default:
                break;
        
        }
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Add aria attributes.
     * 
     * @memberof DropDown
     */
    setupAriaAttributes() {
        // Add ARIA attributes to button.
        if (this.#button) {
            this.#button.setAttribute('aria-haspopup', 'true');
            this.#button.setAttribute('aria-expanded', 'false');
            this.#button.setAttribute('aria-controls', this.#menuId);

            if (this.label) {
                this.#button.setAttribute('aria-label', this.label);
            }
            else {
                const menuText = this.#locales?.menu || 'Menu';
                this.#button.setAttribute('aria-label', menuText);
            }
        }

        const menuContent = this.querySelector('.drop-down-content');

        if (menuContent) {
            menuContent.setAttribute('role', 'menu');
            menuContent.id = this.#menuId;

            // Setup menu items.
            const menuItems = menuContent.querySelectorAll('li');

            menuItems.forEach((item) => {
                item.setAttribute('role', 'menuitem');
                item.setAttribute('tabindex', '-1');

                // If disabled, add aria-disabled.
                if (item.hasAttribute('disabled') || item.classList.contains('disabled')) {
                    item.setAttribute('aria-disabled', 'true');
                }

                // Add aria-label to links if they contain only icons.
                const link = item.querySelector('a');
                if (link) {
                    const hasOnlyIcon = link.querySelector('.dropdown-icon') && !link.textContent.trim();

                    if (hasOnlyIcon) {
                        const menuItemText = this.#locales?.menuItem || 'Menu item';
                        link.setAttribute('aria-label', link.getAttribute('title') || menuItemText);
                    }
                }
            });

            // Retrieves all enabled and visible menu items, excludes items that are disabled or hidden through attributes or classes.
            this.#menuItems = Array.from(this.querySelectorAll('li:not([disabled]):not(.disabled):not([hidden]):not(.hidden)'));
        }
    }

    /**
     * Toggles the visibility of the submenu.
     * 
     * @memberof DropDown
     */
    toggleMenu() {
        this.isActive = !this.isActive;
    }

    /**
     * Validates the content.
     * @returns {boolean} True if valid, false if not. Defaults to true.
     * 
     * @memberof DropDown
     */
    validate() {
        return true;
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Adjusts the menu position to ensure it stays within the viewport.
     * 
     * @private
     */
    #adjustMenuPosition() {
        const menuContent = this.querySelector('.drop-down-content');
        if (!menuContent) {
            return;
        }

        // Reset position and max dimensions.
        this.#resetMenuStyles(menuContent);

        // Use the closest ancestor that visually clips the dropdown (overflow != visible)
        // as the boundary, so the menu cannot extend behind a sibling toolbar or footer that
        // overlays the clipped area. Falls back to the viewport when no clipping ancestor exists.
        const bounds = this.#getClippingBounds();
        const buttonRect = this.#button?.getBoundingClientRect() || new DOMRect();
        const blockThreshold = this.parseCssValue('--menu-block-threshold', 0);
        const inlineThreshold = this.parseCssValue('--menu-inline-threshold', 0);
        const paddingBlock = this.label ? 0 : this.parseCssValue('--menu-padding-block', 0);
        const paddingInline = this.label ? 0 : this.parseCssValue('--menu-padding-inline', 0);

        // The menu's own margins shift the rendered border box outside of the position-defined
        // edge for absolutely positioned elements, so they must be subtracted from the height
        // budget — otherwise the menu's bottom edge ends up past the clipping boundary.
        const menuStyle = getComputedStyle(menuContent);
        const menuMarginTop = parseFloat(menuStyle.marginTop) || 0;
        const menuMarginBottom = parseFloat(menuStyle.marginBottom) || 0;

        // Calculate available space in both directions, relative to the visual clipping bounds.
        const spaceAbove = buttonRect.top - bounds.top - paddingBlock;
        const spaceBelow = bounds.bottom - buttonRect.bottom - paddingBlock;
        const spaceLeft = buttonRect.left - bounds.left - paddingInline;
        const spaceRight = bounds.right - buttonRect.right - paddingInline;

        // Calculate vertical space and position.
        if (spaceBelow > spaceAbove) {
            menuContent.style.top = `${buttonRect.height + paddingBlock}px`;
            menuContent.style.maxHeight = `${spaceBelow - menuMarginTop - blockThreshold}px`;
        }
        else {
            menuContent.style.bottom = `${buttonRect.height + paddingBlock}px`;
            menuContent.style.maxHeight = `${spaceAbove - menuMarginBottom - blockThreshold}px`;
        }

        // Calculate horizontal space and position.
        if (this.#alignRight || spaceRight < spaceLeft) {
            // Align to right and constrain width.
            menuContent.style.right = `${paddingInline}px`;
            menuContent.style.maxWidth = `${spaceLeft + buttonRect.width - inlineThreshold}px`;
        }
        else {
            // Align to left and constrain width.
            menuContent.style.left = `${paddingInline}px`;
            menuContent.style.maxWidth = `${spaceRight + buttonRect.width - inlineThreshold}px`;
        }
    }

    /**
     * Walks up the DOM looking for the closest ancestor that visually clips this element
     * (computed overflow on either axis is not 'visible'), then intersects its rect with the
     * visual viewport so the menu cannot extend below browser chrome (notably iOS Safari, where
     * `100vh` is the large viewport and layout commonly extends past the visible area).
     * @returns {DOMRect}
     *
     * @private
     */
    #getClippingBounds() {
        let clipTop = -Infinity;
        let clipBottom = Infinity;
        let clipLeft = -Infinity;
        let clipRight = Infinity;
        let element = this.parentElement;

        while (element && element !== this.ownerDocument.body) {
            const style = getComputedStyle(element);

            if (style.overflowX !== 'visible' || style.overflowY !== 'visible') {
                const rect = element.getBoundingClientRect();

                clipTop = rect.top;
                clipBottom = rect.bottom;
                clipLeft = rect.left;
                clipRight = rect.right;

                break;
            }

            element = element.parentElement;
        }

        if (clipTop === -Infinity) {
            // No clipping ancestor — fall back to viewport-aligned body bounds.
            const body = this.ownerDocument.body;

            clipTop = 0;
            clipBottom = body.offsetHeight;
            clipLeft = 0;
            clipRight = body.offsetWidth;
        }

        // Intersect with the visual viewport so the dropdown stays within the actually-visible
        // area (iOS Safari address/tab bars shrink the visible viewport without changing layout).
        const view = this.ownerDocument.defaultView;
        const visualViewport = view?.visualViewport;
        const viewportTop = visualViewport?.offsetTop ?? 0;
        const viewportLeft = visualViewport?.offsetLeft ?? 0;
        const viewportHeight = visualViewport?.height ?? view?.innerHeight ?? clipBottom;
        const viewportWidth = visualViewport?.width ?? view?.innerWidth ?? clipRight;

        const top = Math.max(clipTop, viewportTop);
        const bottom = Math.min(clipBottom, viewportTop + viewportHeight);
        const left = Math.max(clipLeft, viewportLeft);
        const right = Math.min(clipRight, viewportLeft + viewportWidth);

        return new DOMRect(left, top, right - left, bottom - top);
    }

    /**
     * Handles the 'enter/space' keyboard interaction.
     * Supports links, buttons, custom elements, and other clickable elements.
     * @param {KeyboardEvent} event The keyboard event data.
     * 
     * @private
     */
    #handleEnter(event) {
        event.preventDefault();
        event.stopPropagation();

        // Get the active element.
        const element = document.activeElement;

        // Function to check if element handles keyboard events.
        const hasKeyboardHandler = (el) => {
            const prototype = el?.constructor?.prototype;

            return Object.prototype.hasOwnProperty.call(prototype || {}, 'onkeydown') ||
                   Object.prototype.hasOwnProperty.call(prototype || {}, 'onkeypress') ||
                   el?.hasAttribute('onkeydown') ||
                   el?.hasAttribute('onkeypress');
        };

        // If element handles keyboard events, let it handle the event.
        if (coreUI.isInteractive(element) && hasKeyboardHandler(element)) {
            const keyEvent = new KeyboardEvent('keydown', {
                bubbles: true,
                cancelable: true,
                key: event.key
            });

            element.dispatchEvent(keyEvent);

            return;
        }

        // Otherwise, use click() as fallback.
        if (coreUI.isInteractive(element)) {
            element.click();
            this.isActive = false;

            return;
        }

        // Check children with the same logic.
        const customElements = element?.querySelectorAll('*');
        const interactiveChild = [...(customElements ?? [])].find((el) => {
            return coreUI.isInteractive(el);
        });

        if (interactiveChild) {
            if (hasKeyboardHandler(interactiveChild)) {
                const keyEvent = new KeyboardEvent('keydown', {
                    bubbles: true,
                    cancelable: true,
                    key: event.key
                });

                interactiveChild.dispatchEvent(keyEvent);
            }
            else {
                interactiveChild.click();
            }

            this.isActive = false;
        }
    }

    /**
     * Closes the menu if a click is detected outside of the component.
     * @param {Event} event The click event that might close the menu.
     * 
     * @private
     */
    #outsideClickHandler(event) {
        if (!this.isActive) {
            return;
        }

        if (!this.contains(event.target) || event.target === this.#overlay) {
            this.isActive = false;
        }
    }

    /**
     * Resets all positioning and dimension styles of the menu.
     * @param {HTMLElement} menuContent The dropdown content element.
     * 
     * @private
     */
    #resetMenuStyles(menuContent) {
        const stylesToReset = ['left', 'right', 'top', 'bottom', 'maxHeight', 'maxWidth', 'minWidth'];
        stylesToReset.forEach((style) => {
            menuContent.style[style] = '';
        });
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

/**
 * Register web component.
 * Defines the custom element 'drop-down' and maps it to the DropDown class.
 */
customElements.define(DropDown.tagName, DropDown);
